import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset/${token}`);
        setInfo(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || "Reset link invalid.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/reset/${token}`, { password });
      if (data.access_token) localStorage.setItem("sellerhub_token", data.access_token);
      await refresh();
      nav(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="container-x" style={{ padding: "64px 0", color: "var(--text-muted)" }}>Loading…</div>;

  if (!info) {
    return (
      <div className="container-x" style={{ padding: "64px 0", display: "flex", justifyContent: "center" }}>
        <div className="card" style={{ padding: 40, maxWidth: 460, textAlign: "center" }} data-testid="reset-invalid">
          <span className="overline">Link unavailable</span>
          <h1 className="font-serif" style={{ fontSize: 28, margin: "12px 0", fontWeight: 500 }}>This reset link is invalid or expired.</h1>
          <p style={{ color: "var(--text-muted)" }}>{err}</p>
          <Link to="/forgot" className="btn-primary" style={{ marginTop: 18, display: "inline-flex" }} data-testid="request-new-reset">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x" style={{ padding: "64px 0", display: "flex", justifyContent: "center" }}>
      <div className="card" style={{ width: "100%", maxWidth: 440, padding: 40 }}>
        <span className="overline">Reset password</span>
        <h1 className="font-serif" style={{ fontSize: 32, margin: "8px 0 12px", fontWeight: 500, letterSpacing: "-0.01em" }}>
          Choose a new password
        </h1>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "0 0 20px" }}>
          For <b style={{ color: "var(--text)" }}>{info.email}</b>
        </p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, display: "block", marginBottom: 6 }}>New password</label>
            <input data-testid="reset-password-input" className="input-field" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
          </div>
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, display: "block", marginBottom: 6 }}>Confirm</label>
            <input data-testid="reset-confirm-input" className="input-field" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {err && <div data-testid="reset-error" style={{ color: "var(--error)", fontSize: 14 }}>{err}</div>}
          <button data-testid="reset-submit-btn" className="btn-primary" type="submit" disabled={busy} style={{ justifyContent: "center" }}>
            {busy ? "Saving…" : (<><CheckCircle2 size={16} /> Save & log in</>)}
          </button>
        </form>
      </div>
    </div>
  );
}
